<script setup>
import { computed, ref, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { Offcanvas } from "bootstrap";

// Refs for DOM elements
const offcanvasElement = ref(null);
const legendInput = ref(null);
const bsOffcanvas = ref(null);

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const router = proxy.$router;

// --- Computed ---

const formData = computed(() => store.state.componentFields.fieldsetFormData);
const fieldsetId = computed(() => formData.value?.id);
const title = computed(() => fieldsetId.value ? 'Edit fieldset' : 'New fieldset');

// --- Methods ---

const onComplete = async () => {
    await store.dispatch('componentFields/reloadFieldsets');
    bsOffcanvas.value?.hide();
};

const handleSubmit = () => {
    if (fieldsetId.value) {
        store.dispatch('request/patch', {
            url: `api/component/fieldset/${fieldsetId.value}`,
            postData: { legend: formData.value.legend }
        }).then(onComplete);
    } else {
        store.dispatch('request/post', {
            url: 'api/component/fieldset',
            postData: {
                componentId: store.state.componentFields.componentId,
                legend: formData.value.legend
            }
        }).then((obj) => {
            router.push({
                name: 'componentFields',
                params: { fieldsetId: obj.data.id, fieldId: null }
            });
        }).then(onComplete);
    }
};

const deleteFieldset = () => {
    // Close offcanvas so the ConfirmModal backdrop/focus works correctly
    bsOffcanvas.value?.hide();

    const idToDelete = fieldsetId.value;
    const legend = formData.value.legend;

    store.dispatch('modals/show', {
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: `Deleting the fieldset "${legend}"`,
            modalBody: `
                <div class="alert alert-danger">This action will result in loss of data.</div>
                <p>This action <strong>cannot</strong> be undone. All fields of the fieldset <strong>${legend}</strong> will be permanently deleted.</p>
            `,
            confirmStr: legend,
            onConfirm: () => {
                store.dispatch('request/delete', { url: `api/component/fieldset/${idToDelete}` })
                    .then(onComplete)
                    .then(() => {
                        router.push({
                            name: 'componentFields',
                            params: { fieldsetId: null, fieldId: null }
                        });
                    });
            }
        }
    });
};

// --- Lifecycle Hooks ---

onMounted(() => {
    bsOffcanvas.value = new Offcanvas(offcanvasElement.value);

    // Event listeners using the ref
    offcanvasElement.value.addEventListener('shown.bs.offcanvas', () => {
        legendInput.value?.select();
    });

    offcanvasElement.value.addEventListener('hidden.bs.offcanvas', () => {
        // Clear form data on close
        store.state.componentFields.fieldsetFormData = {};
    });
});

onUnmounted(() => {
    // Cleanup to prevent memory leaks
    bsOffcanvas.value?.dispose();
});
</script>

<template>
    <div
        class="offcanvas offcanvas-start"
        tabindex="-1"
        id="contentTypeFieldsetOffcanvas"
        ref="offcanvasElement"
        aria-labelledby="contentTypeFieldsetOffcanvasLabel"
    >
        <div class="offcanvas-header">
            <h5 class="offcanvas-title" id="contentTypeFieldsetOffcanvasLabel">{{ title }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>

        <div class="offcanvas-body">
            <form @submit.prevent="handleSubmit">
                <input
                    v-model="formData.legend"
                    ref="legendInput"
                    name="legend"
                    class="form-control"
                    placeholder="Legend"
                >
                <div class="mt-3 d-flex">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="!formData.legend"
                    >
                        Save
                    </button>
                </div>
            </form>
        </div>

        <div class="p-3 text-end">
            <button v-if="fieldsetId" class="btn btn-danger" @click.prevent="deleteFieldset">
                Delete
            </button>
        </div>
    </div>
</template>